import { z } from 'zod';

const roleEnum = z.enum(
  ['owner', 'admin', 'project-manager', 'field_officer', 'accountant', 'store-manager', 'telecaller', 'designer', 'sales_executive', 'site_manager', 'inventory_manager', 'carpenter', 'electrician', 'painter', 'plumber'],
  { errorMap: () => ({ message: 'Please select a valid role' }) }
);

const TRADES = ['carpenter', 'electrician', 'painter', 'plumber'];

/** 8–10 chars, at least one uppercase (A–Z) and one special (non letter/digit). */
export const teamMemberPasswordSchema = z
  .string()
  .trim()
  .min(8, 'Password must be at least 8 characters')
  .max(10, 'Password must be at most 10 characters')
  .regex(/[A-Z]/, 'Include at least one uppercase letter (A–Z)')
  .regex(/[^A-Za-z0-9]/, 'Include at least one special character (e.g. !@#$%)');

const normalizePhoneDigits = (value) =>
  String(value ?? '')
    .trim()
    .replace(/[^\d]/g, ''); // allow spaces, dashes, etc. in UI input

const normalizeJoiningDate = (value) => {
  const s = String(value ?? '').trim();
  if (!s) return '';
  // Accept both `YYYY-MM-DD` (native date input) and `DD-MM-YYYY` (typed/pasted).
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return s;
};

const baseFields = {
  firstName: z.string().min(3, 'First name must be at least 3 characters'),
  lastName: z.string().optional().or(z.literal('')),
  phone: z.string().length(10, 'Phone number must be exactly 10 digits').regex(/^\d+$/, 'Phone number must contain only digits'),
  email: z.string().email('Please enter a valid email address'),
  employeeId: z.string().min(1, 'Employee ID is required'),
  role: roleEnum,
  installationTrade: z.string().optional().or(z.literal('')),
  joiningDate: z.string().min(1, 'Joining date is required'),
  workLocation: z.string().min(1, 'Work location is required'),
  loginId: z.string().optional().or(z.literal('')),
  notes: z.string().optional().or(z.literal(''))
};

const requireTradeForInstallationMember = (data, ctx) => {
  if (data.role !== 'field_officer') return;
  const t = data.installationTrade;
  if (!t || !TRADES.includes(t)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Select installation type: Carpenter, Electrician, Painter, or Plumber',
      path: ['installationTrade']
    });
  }
};

export const employeeCreateSchema = z
  .object({
    ...baseFields,
    password: teamMemberPasswordSchema
  })
  .superRefine(requireTradeForInstallationMember);

export const employeeUpdateSchema = z
  .object({
    ...baseFields,
    password: z.union([z.literal(''), teamMemberPasswordSchema]).optional()
  })
  .superRefine(requireTradeForInstallationMember);
